/**
 * Plain copy for a subscription's error and coverage codes.
 *
 * The API never sends the server's diagnostic text to a tenant — it can embed
 * the message broker's raw response body — only `lastErrorCode` and the
 * coverage report's reason codes. This is the one place those codes become
 * words. A typed map, not a classifier over free text: copy keyed on
 * API-owned codes stays stable when log wording changes. A coverage reason
 * code may follow its sentence as secondary muted mono (the SDK and support
 * see the same code); the server's free text never reaches the UI. Mirrors
 * api/src/services/subscriptionErrors.ts and subscriptionMatching.ts.
 */

export type SubscriptionErrorCode =
  | 'BROKER_REFUSED'
  | 'PROVISIONING_FAILED'
  | 'QUEUE_ORPHANED'
  | 'LEGACY_RESUBSCRIBE';

export interface SubscriptionErrorCopy {
  title: string;
  body: string;
  /** Whether support needs the subscription ID quoted: the platform, not the tenant, has to look. */
  quoteId: boolean;
}

const PLATFORM_FAILURE: SubscriptionErrorCopy = {
  title: "Couldn't set up this subscription",
  body: "The platform couldn't set up this subscription. Delete it and try again; if it keeps failing, contact support with this ID.",
  quoteId: true,
};

const RECREATE: SubscriptionErrorCopy = {
  title: 'This subscription needs to be recreated',
  body: 'Delete this subscription and subscribe again.',
  quoteId: false,
};

export const SUBSCRIPTION_ERROR_COPY: Record<SubscriptionErrorCode, SubscriptionErrorCopy> = {
  BROKER_REFUSED: PLATFORM_FAILURE,
  PROVISIONING_FAILED: PLATFORM_FAILURE,
  QUEUE_ORPHANED: RECREATE,
  LEGACY_RESUBSCRIBE: {
    ...RECREATE,
    body: 'It was created before the current delivery system. Delete this subscription and subscribe again.',
  },
};

/** Copy for an error code; an unknown or missing code gets the generic platform failure. */
export function describeSubscriptionError(code: string | null | undefined): SubscriptionErrorCopy {
  return (code && SUBSCRIPTION_ERROR_COPY[code as SubscriptionErrorCode]) || PLATFORM_FAILURE;
}

export type CoverageReason =
  | 'LENS_NOT_ACCESSIBLE'
  | 'NO_SENSORS_IN_REGION'
  | 'NO_TEMPLATE_EMITS_IT'
  | 'NO_COMPATIBLE_SENSOR';

/** Where an uncovered event type can be fixed, if anywhere a tenant can reach. */
export type CoverageFix = 'lenses' | 'sensors';

export const COVERAGE_REASON_COPY: Record<CoverageReason, { text: string; fix: CoverageFix }> = {
  LENS_NOT_ACCESSIBLE: { text: 'The lens is no longer shared with you', fix: 'lenses' },
  NO_SENSORS_IN_REGION: { text: 'No sensors cover this area', fix: 'sensors' },
  NO_TEMPLATE_EMITS_IT: { text: 'No template in this lens produces this event type', fix: 'lenses' },
  NO_COMPATIBLE_SENSOR: { text: 'No sensor here can run a template that produces it', fix: 'sensors' },
};

/** Copy for a coverage reason; an unknown or missing reason gets a neutral line and no link. */
export function describeCoverageReason(
  reason: string | null | undefined,
): { text: string; fix: CoverageFix | null } {
  const known = reason ? COVERAGE_REASON_COPY[reason as CoverageReason] : undefined;
  return known ?? { text: 'Not covered yet', fix: null };
}
